import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useI18n, pick } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import HeroOrb from "@/components/HeroOrb";

export default function Hero() {
  const { lang } = useI18n();
  const { content } = useContent();
  const hero = content?.hero;
  if (!hero) return null;
  const data = pick(hero, lang, hero.ru);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="home" className="relative overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-44 lg:pb-32" data-testid="hero-section">
      {/* Soft theme-aware background */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 70% 30%, hsl(var(--accent) / 0.18), transparent 60%), radial-gradient(ellipse 70% 50% at 20% 70%, hsl(var(--primary) / 0.15), transparent 60%)",
          }}
        />
      </div>

      {/* Floating accents */}
      <motion.div
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/20 blur-3xl pointer-events-none"
        animate={{ y: [0, 30, 0], x: [0, -20, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-0 left-1/4 w-72 h-72 rounded-full bg-accent/20 blur-3xl pointer-events-none"
        animate={{ y: [0, -40, 0], x: [0, 30, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 pl-2 pr-3 sm:pr-4 py-1.5 rounded-full border border-border/70 bg-card/60 backdrop-blur text-foreground/80 text-[11px] sm:text-sm font-medium mb-5 sm:mb-7"
          >
            <span className="inline-flex items-center justify-center size-5 rounded-full bg-accent/15 text-accent">
              <Sparkles className="size-3" />
            </span>
            {data.eyebrow}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-[2rem] leading-[1.1] sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-5 sm:mb-6"
            data-testid="hero-title"
          >
            <span className="text-gradient">{data.title}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl mb-8 sm:mb-10"
            data-testid="hero-subtitle"
          >
            {data.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3"
          >
            <button
              onClick={() => scrollTo("vacancies")}
              className="group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-foreground text-background font-semibold text-sm hover:translate-y-[-2px] transition-transform"
              data-testid="hero-cta-primary"
            >
              {data.cta_primary}
              <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => scrollTo("process")}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-border bg-background/60 backdrop-blur text-foreground font-semibold text-sm hover:bg-muted transition-colors"
              data-testid="hero-cta-secondary"
            >
              {data.cta_secondary}
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <HeroOrb />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
